import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Grid,
  Typography,
} from '@mui/material';
import { SparkDialog } from 'sharedComponents/UI';
import { BackIcon, PlusIcon, SettingsIcon } from 'components/SVG';
import ListIcon from 'components/SVG/ListIcon';
import LinkToStepView from './LinkToStepView';
import StartFromScratchView from './StartFromScratchView';

type AddNodeModalProps = {
  open: boolean;
  onClose: () => void;
  boardID: string | null | undefined;
};

function AddNodeModal({ open, onClose, boardID }: AddNodeModalProps) {
  const [selectedNodeType, setSelectedNodeType] = React.useState<
    'default' | 'linkToStep' | 'startFromScratch'
  >('default');

  React.useEffect(() => {
    return () => setSelectedNodeType('default');
  }, [boardID]);

  const list = [
    {
      id: '1',
      title: 'Link to Step',
      key: 'linkToStep',
      icon: <ListIcon color='#6267AC' />,
      description: 'Use step output',
    },
    {
      id: '2',
      title: 'Custom',
      key: 'startFromScratch',
      icon: <SettingsIcon stroke='#6267AC' />,
      description: 'Choose output type',
    },
  ];

  const selectNodeTypeModalContent = {
    createTitle: function ({
      icon,
      title,
    }: {
      icon: React.ReactNode;
      title: string;
    }) {
      return (
        <>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            gap={2}
          >
            <Box onClick={() => setSelectedNodeType('default')}>{icon}</Box>
            <Typography>{title}</Typography>
          </Box>
        </>
      );
    },
    default: {
      title: 'Add Output',
      icon: <PlusIcon color='white' />,
      content: (
        <>
          <Grid container justifyContent='center' gap={2} wrap='nowrap'>
            {list?.map((type) => (
              <Grid key={type.id} item md={5} lg={4}>
                <ButtonBase
                  disableRipple={true}
                  onClick={() => setSelectedNodeType(type.key as any)}
                  sx={{ width: '100%' }}
                >
                  <Card
                    elevation={0}
                    style={{ maxWidth: '100%' }}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: (theme) =>
                        `1px solid ${theme.palette.borderColor}`,
                      '&:hover': {
                        borderColor: (theme) => theme.palette.primary.main,
                      },
                      '&:active': { transform: 'scale(0.98)' },
                      '.MuiCardHeader-avatar': {
                        alignSelf: 'flex-start',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        paddingY: '20px !important',
                      }}
                    >
                      <Box
                        borderRadius={1}
                        sx={{
                          aspectRatio: 1,
                          width: '50px',
                          alignSelf: 'center',
                          bgcolor: (theme) => theme.palette.primary.light,
                          display: 'flex',
                          justifyContent: 'center',
                          justifySelf: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {type.icon}
                      </Box>
                      <Box>
                        <Typography gutterBottom variant='h5'>
                          {type.title}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {type.description}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </ButtonBase>
              </Grid>
            ))}
          </Grid>
        </>
      ),
    },
    startFromScratch: {
      title: 'Output Type',
      icon: <BackButton />,
      content: <StartFromScratchView boardID={boardID} onClose={onClose} />,
    },
    linkToStep: {
      title: 'Link to Step',
      icon: <BackButton />,
      content: <LinkToStepView boardID={boardID} onClose={onClose} />,
    },
  };
  function BackButton() {
    return (
      <Button
        onClick={() => setSelectedNodeType('default')}
        sx={{
          minWidth: 'unset',
          width: '40px',
          height: '40px',
          padding: 0,
          borderWidth: '1px',
          borderRadius: '14px',
          aspectRatio: 1,
          backgroundColor: '#fff',
          color: 'var( --color-primary)',
          '&:hover': {
            backgroundColor: '#fff',
            color: 'var( --color-primary)',
          },
        }}
        variant='contained'
      >
        <BackIcon stroke='currentColor' />
      </Button>
    );
  }
  return (
    <SparkDialog
      title={selectNodeTypeModalContent.createTitle({
        icon: selectNodeTypeModalContent[selectedNodeType].icon,
        title: selectNodeTypeModalContent[selectedNodeType].title,
      })}
      open={open}
      onClose={onClose}
      dialogContentStyles={{ position: 'relative' }}
    >
      {selectNodeTypeModalContent[selectedNodeType].content}
    </SparkDialog>
  );
}

export default AddNodeModal;
